import { useMemo } from "react";
import type { WidgetFaceProps } from "@/components/shapes/shared/widget-face";
import {
  WIDGET_DISPLAY,
  WIDGET_MUTED,
  WidgetDot,
  WidgetEmpty,
  WidgetFaceRoot,
  WidgetMore,
  WidgetRule,
} from "@/components/shapes/shared/widget-face-parts";
import { fitRows } from "@/components/shapes/shared/widget-rows";
import { Icons } from "@/components/ui/icons";
import { useT } from "@/i18n";
import { framesTaskboardMessages } from "@/i18n/messages/frames-taskboard";
import { cn } from "@/lib/cn";
import { useTasksStore } from "@/stores/tasks-store";
import { useFrameBoardTasks } from "./task-display-filters";
import {
  STATUS_DOT_COLORS,
  TASK_STATUS_LABEL_KEYS,
} from "./task-metadata-chips";
import {
  taskboardStatusGroups,
  type TaskboardStatusGroup,
} from "./taskboard-status-groups";

/** Groups each size shows, most urgent first. */
const GROUPS = { small: 2, medium: 3, large: 3 } as const;
/** Task lines per group on the large face. */
const GROUP_ROWS = 2;
/** Most urgent tasks on the medium face. */
const URGENT_ROWS = 2;

/** The board's icon and title, with its active count beside it. */
function BoardHeader({ title, active }: { title: string; active?: string }) {
  return (
    <p className="flex min-w-0 items-center gap-1.5">
      <Icons.Kanban
        className={cn("h-3.5 w-3.5 shrink-0", WIDGET_MUTED)}
        aria-hidden="true"
      />
      <span className={cn("truncate", WIDGET_DISPLAY.s)}>{title}</span>
      {active && (
        <span className={cn("ml-auto shrink-0", WIDGET_MUTED)}>{active}</span>
      )}
    </p>
  );
}

/** A task's line with its status dot. */
function TaskLine({ title, color }: { title: string; color: string }) {
  return (
    <p className="flex min-w-0 items-center gap-[7px]">
      <WidgetDot color={color} />
      <span className="truncate">{title}</span>
    </p>
  );
}

/**
 * The project's task board on the desktop, open work by status in urgency
 * order. Small: how many are active and the two most urgent statuses.
 * Medium: a chip per status and the most urgent tasks. Large: each status
 * with its first tasks. Quiet until the project's tasks have loaded.
 */
export function TaskboardWidgetFace({ frame, size }: WidgetFaceProps) {
  const t = useT(framesTaskboardMessages);
  // The board's own project's tasks (Home's board: every project's).
  const tasks = useFrameBoardTasks(frame);
  const loaded = useTasksStore((state) => state.loaded);
  const allGroups = useMemo(
    () => taskboardStatusGroups(tasks ?? [], ""),
    [tasks],
  );
  // As the board itself names it.
  const title = frame.title.trim() || t("boardTitleFallback");
  if (!loaded || !tasks) {
    // Until the fetch settles and the window shows a project nothing is
    // known, so the face says nothing rather than "No open tasks".
    return (
      <WidgetFaceRoot frame={frame} size={size}>
        <BoardHeader title={title} />
        <div
          className="flex-1"
          aria-busy="true"
          data-testid="taskboard-widget-loading"
        />
      </WidgetFaceRoot>
    );
  }
  const urgent = allGroups[0];
  if (!urgent) {
    return <WidgetEmpty frame={frame} size={size} message={t("noOpenTasks")} />;
  }
  const active = allGroups.reduce((sum, group) => sum + group.tasks.length, 0);
  const groups = allGroups.slice(0, GROUPS[size]);
  const label = (group: TaskboardStatusGroup) =>
    t(TASK_STATUS_LABEL_KEYS[group.status]);
  const color = (group: TaskboardStatusGroup) =>
    STATUS_DOT_COLORS[group.status];

  if (size === "small") {
    return (
      <WidgetFaceRoot frame={frame} size={size}>
        <BoardHeader title={title} />
        <p className="mt-0.5 flex items-baseline gap-1.5">
          <span
            className={cn("tabular-nums", WIDGET_DISPLAY.xl)}
            data-testid="taskboard-widget-active"
          >
            {active}
          </span>
          <span className={WIDGET_MUTED}>{t("active")}</span>
        </p>
        <div className="mt-auto grid gap-1">
          {groups.map((group) => (
            <TaskLine
              key={group.status}
              color={color(group)}
              title={`${group.tasks.length} ${label(group)}`}
            />
          ))}
        </div>
      </WidgetFaceRoot>
    );
  }

  const activeCount = t("activeCount", { count: active });

  if (size === "medium") {
    return (
      <WidgetFaceRoot frame={frame} size={size}>
        <BoardHeader title={title} active={activeCount} />
        <div className="flex flex-wrap gap-1.5">
          {groups.map((group) => (
            <span
              key={group.status}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-[var(--widget-fill,var(--card-line))] px-[9px] py-[3px] text-appkit-caption"
              data-testid={`taskboard-widget-chip-${group.status}`}
            >
              <WidgetDot color={color(group)} />
              {label(group)} {group.tasks.length}
            </span>
          ))}
        </div>
        <div className="mt-auto grid gap-1.5">
          {urgent.tasks.slice(0, URGENT_ROWS).map((task) => (
            <TaskLine key={task.id} color={color(urgent)} title={task.title} />
          ))}
        </div>
      </WidgetFaceRoot>
    );
  }

  return (
    <WidgetFaceRoot frame={frame} size={size}>
      <BoardHeader title={title} active={activeCount} />
      {groups.map((group, index) => {
        const { shown, more } = fitRows(group.tasks, GROUP_ROWS);
        return (
          <div
            key={group.status}
            className="mt-1 grid gap-[5px]"
            data-testid={`taskboard-widget-group-${group.status}`}
          >
            {index > 0 && <WidgetRule className="mb-1" />}
            <p className="flex min-w-0 items-center gap-1.5 font-semibold">
              <WidgetDot color={color(group)} />
              <span className="truncate">{label(group)}</span>
              <span
                className={cn("ml-auto font-normal tabular-nums", WIDGET_MUTED)}
              >
                {group.tasks.length}
              </span>
            </p>
            {shown.map((task) => (
              <p key={task.id} className="truncate pl-[13px]">
                {task.title}
              </p>
            ))}
            <WidgetMore count={more} className="pl-[13px]" />
          </div>
        );
      })}
    </WidgetFaceRoot>
  );
}
